import { useRef, useState } from 'react';
import { SearchFieldWrapper, ResultWrapper } from './styled';
import type { Theme } from '@simplywallst/ui-core';
import { Textfield } from '@simplywallst/ui-core';
import type { ReactNode } from 'react';
import { SearchIcon } from '@/components/Search';
import { Modal } from '@simplywallst/ui-core';
import { RouterLink } from '@/utilities/redux-first-router.export';
import { useDispatch } from 'react-redux';
import { cacheResults } from '@/components/Search/redux/routines';
import loadable from '@loadable/component';
import SearchFieldPostfix from '@/components/SearchFieldPostfix';
const AlgoliaSearch = loadable(
  () => import('@/components/Search/components/AlgoliaSearch')
);

interface Props {
  name?: string;
  value?: string;
  defaultState?: ReactNode;
  placeholder?: string;
  theme?: Theme['appTheme'];
  error?: string;
  onClick?: (company: { id: string; name: string }) => void;
  onClose?: () => void;
  onBlur?: (value: string) => void;
  onReset?: () => void;
}

export const MobileSearch = ({
  name,
  value,
  defaultState,
  placeholder,
  theme,
  error,
  onClick,
  onClose,
  onBlur,
  onReset,
}: Props) => {
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [inputValue, setInputValue] = useState(value || '');
  const dispatch = useDispatch();
  const input = useRef<HTMLInputElement>(null);

  const handleReset = () => {
    const { current } = input;
    if (current) current.focus();
    setQuery('');
    setInputValue('');
    onReset?.();
  };

  return (
    <>
      <SearchFieldWrapper>
        <Textfield
          value={inputValue}
          ref={input}
          styleType={theme === 'light' ? 'standardReverse' : 'standard'}
          name={name}
          type="search"
          error={error}
          placeholder={placeholder}
          prefix={<SearchIcon />}
          postfix={
            <SearchFieldPostfix
              styleType={theme === 'light' ? 'light' : 'dark'}
              searchQuery={inputValue}
              onReset={handleReset}
            />
          }
          onFocus={() => {
            setShowSearch(true);
          }}
          readOnly
        />
      </SearchFieldWrapper>
      {showSearch && (
        <Modal isNaked onSecondaryClick={() => setShowSearch(false)}>
          <AlgoliaSearch
            name={name}
            query={query}
            defaultState={defaultState}
            theme={theme}
            error={error}
            onBlur={onBlur}
            onClose={() => {
              setShowSearch(false);
              onClose?.();
            }}
            onSearchResult={(results, query) => {
              setQuery(query);
              dispatch(cacheResults.trigger(results));
            }}
            primaryWrapper={(child, result) => {
              return onClick ? (
                <ResultWrapper
                  onClick={() => {
                    onClick({
                      id: result.uniqueSymbol,
                      name: result.name,
                    });
                    setShowSearch(false);
                    setInputValue(result.name);
                  }}
                >
                  {child}
                </ResultWrapper>
              ) : (
                <RouterLink to={result.canonicalUrl} tabIndex={0}>
                  {child}
                </RouterLink>
              );
            }}
            secondaryWrapper={(child, listing) => {
              return onClick ? (
                <ResultWrapper
                  onClick={() => {
                    onClick({
                      id: listing.uniqueSymbol,
                      name: listing.uniqueSymbol,
                    });
                    setShowSearch(false);
                    setInputValue(listing.uniqueSymbol);
                  }}
                >
                  {child}
                </ResultWrapper>
              ) : (
                <RouterLink to={listing.canonicalUrl} tabIndex={0}>
                  {child}
                </RouterLink>
              );
            }}
          />
        </Modal>
      )}
    </>
  );
};
